import React, { useMemo, useState } from 'react';
import { Button, Card, Input, Segmented, Space, Table, Tag, Typography, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../config/api';
import { ReloadOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons';
import { RetainIQFollowUpManageModal, type FollowUpRow } from './RetainIQFollowUpManageModal';
import { formatRetainIQDate } from './retainiqDates';
import './retainiq.css';

const FollowUpCellText: React.FC<{ value?: string | null }> = ({ value }) => {
  const text = value?.trim() ? value.trim() : '—';
  if (text === '—') {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
        —
      </Typography.Text>
    );
  }
  return (
    <Tooltip title={text} placement="topLeft">
      <Typography.Text ellipsis style={{ display: 'block', maxWidth: '100%', fontSize: 13 }}>
        {text}
      </Typography.Text>
    </Tooltip>
  );
};

function followUpRowSearchHaystack(row: FollowUpRow): string {
  const dueLabel =
    row.followup_status === 'completed'
      ? 'done'
      : (row.days_until ?? 0) < 0
        ? 'overdue'
        : (row.days_until ?? 0) === 0
          ? 'today'
          : 'upcoming';
  const ackLabel = row.followup_status === 'acknowledged' ? 'yes acknowledged' : '';
  const notesCount = Array.isArray(row.followup_notes_log) ? row.followup_notes_log.length : 0;
  return [
    formatRetainIQDate(row.followup_date),
    row.followup_date,
    dueLabel,
    ackLabel,
    row.followup_acknowledged_by_name,
    notesCount ? String(notesCount) : '',
    row.business_name,
    row.agent_name,
    row.call_type,
    row.followup_action,
    row.ticket_number,
    row.followup_status,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export const RetainIQFollowUps: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>('due');
  const [searchTerm, setSearchTerm] = useState('');
  const [manageRow, setManageRow] = useState<FollowUpRow | null>(null);

  const { data, isLoading, refetch } = useQuery(
    ['retain-iq-follow-ups', status],
    async () => {
      const res = await api.get('/api/retain-iq/follow-ups', { params: { status } });
      return res.data as { follow_ups: FollowUpRow[] };
    },
    { keepPreviousData: true }
  );

  const filteredFollowUps = useMemo(() => {
    const rows = data?.follow_ups || [];
    const q = searchTerm.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => followUpRowSearchHaystack(row).includes(q));
  }, [data?.follow_ups, searchTerm]);

  const columns: ColumnsType<FollowUpRow> = useMemo(
    () => [
      {
        title: 'Follow-up',
        dataIndex: 'followup_date',
        key: 'followup_date',
        width: 115,
        render: (d: string) => formatRetainIQDate(d) || '—',
      },
      {
        title: 'Due',
        key: 'due',
        width: 90,
        render: (_: unknown, row) => {
          if (row.followup_status === 'completed') return <Tag color="green">Done</Tag>;
          const days = row.days_until ?? 0;
          if (days < 0) return <Tag color="red">Overdue</Tag>;
          if (days === 0) return <Tag color="orange">Today</Tag>;
          return <Tag color="blue">Upcoming</Tag>;
        },
      },
      {
        title: 'Ack',
        key: 'ack',
        width: 72,
        render: (_: unknown, row) => {
          if (row.followup_status === 'acknowledged') {
            return (
              <Tooltip title={row.followup_acknowledged_by_name || 'Acknowledged'}>
                <Tag color="blue">Yes</Tag>
              </Tooltip>
            );
          }
          return <Typography.Text type="secondary">—</Typography.Text>;
        },
      },
      {
        title: 'Notes',
        key: 'notes',
        width: 64,
        align: 'center',
        render: (_: unknown, row) => {
          const n = Array.isArray(row.followup_notes_log) ? row.followup_notes_log.length : 0;
          return n > 0 ? <Tag>{n}</Tag> : <Typography.Text type="secondary">—</Typography.Text>;
        },
      },
      {
        title: 'Business',
        dataIndex: 'business_name',
        key: 'business_name',
        ellipsis: { showTitle: false },
        render: (name: string) => <FollowUpCellText value={name} />,
      },
      {
        title: 'Agent',
        dataIndex: 'agent_name',
        key: 'agent_name',
        width: 120,
        ellipsis: { showTitle: false },
        render: (name: string) => <FollowUpCellText value={name} />,
      },
      {
        title: 'Call type',
        dataIndex: 'call_type',
        key: 'call_type',
        width: 130,
        ellipsis: { showTitle: false },
        render: (type: string) => <FollowUpCellText value={type} />,
      },
      {
        title: 'Action / owner',
        dataIndex: 'followup_action',
        key: 'followup_action',
        ellipsis: { showTitle: false },
        render: (action: string) => <FollowUpCellText value={action} />,
      },
      {
        title: 'Manage',
        key: 'actions',
        width: 96,
        fixed: 'right',
        align: 'center',
        render: (_: unknown, row) => (
          <Tooltip title="Acknowledge, add notes, reschedule, or mark complete">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setManageRow(row);
              }}
              style={{ paddingInline: 4 }}
            >
              Manage
            </Button>
          </Tooltip>
        ),
      },
    ],
    []
  );

  return (
    <div>
      <Typography.Title level={4}>Follow-ups</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
        Click a row or the <EditOutlined /> Manage action to acknowledge, add notes, reschedule, or mark complete. Hover
        truncated cells to see the full text.
      </Typography.Paragraph>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Segmented
            value={status}
            onChange={(v) => setStatus(String(v))}
            options={[
              { label: 'Due (today + overdue)', value: 'due' },
              { label: 'Overdue', value: 'overdue' },
              { label: 'Today', value: 'today' },
              { label: 'Upcoming', value: 'upcoming' },
              { label: 'Acknowledged', value: 'acknowledged' },
              { label: 'Completed', value: 'completed' },
              { label: 'All open', value: 'all' },
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            Refresh
          </Button>
          <Input
            placeholder="Search follow-ups by date, business, agent, call type, action, status…"
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            style={{ minWidth: 320, maxWidth: 480 }}
          />
        </Space>
      </Card>
      <Table<FollowUpRow>
        className="retain-iq-reports-table"
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={filteredFollowUps}
        scroll={{ x: 1100 }}
        tableLayout="fixed"
        onRow={(record) => ({
          onClick: () => setManageRow(record),
        })}
        rowClassName={(record) =>
          manageRow?.id === record.id
            ? 'retain-iq-reports-row retain-iq-reports-row--active'
            : 'retain-iq-reports-row'
        }
        pagination={{ pageSize: 50, showSizeChanger: true }}
      />

      <RetainIQFollowUpManageModal
        open={!!manageRow}
        followUp={manageRow}
        onClose={() => setManageRow(null)}
        onUpdated={(row) => setManageRow(row)}
        onOpenTicket={(n) => navigate(`/workspace/number/${n}`)}
      />
    </div>
  );
};
